/**
 * Admin Alert Service
 * 
 * Service สำหรับดึงข้อมูลแจ้งเตือนสำหรับ Admin Panel
 * ⚠️ แยกจาก Member Notification (Notification class ใน Parse)
 */

import {
  queryClass,
  callCloudFunction,
} from "@/services/adapters/parse/parse-client";
import type {
  AdminAlert,
  AdminAlertData,
  AdminAlertCounts,
} from "./admin-alert.types";
import moment from "moment";

// ============================================
// Service
// ============================================

export const AdminAlertService = {
  /**
   * ดึงข้อมูลแจ้งเตือนทั้งหมดสำหรับ Admin
   */
  async getAlerts(
    sessionToken: string,
    options: {
      limit?: number;
      includeNewMembers?: boolean;
      newMembersHours?: number; // จำนวนชั่วโมงที่ถือว่าเป็นสมาชิกใหม่ (default: 24)
    } = {}
  ): Promise<AdminAlertData> {
    const {
      limit = 20,
      includeNewMembers = true,
      newMembersHours = 24,
    } = options;

    console.log("🔔 [AdminAlertService] Fetching admin alerts...");

    try {
      // ✅ ดึง viewed alerts จาก backend (AdminViewedNotification)
      let viewedAlerts: Record<string, string[]> = {};
      try {
        viewedAlerts =
          (await callCloudFunction(
            "getViewedNotifications",
            {},
            sessionToken
          )) || {};
        console.log(
          "👁️ [AdminAlertService] Viewed alerts:",
          viewedAlerts
        );
      } catch (error) {
        console.warn(
          "⚠️ [AdminAlertService] Failed to fetch viewed alerts:",
          error
        );
        // Continue without filtering if error
      }

      // 1. ดึง Pending Deposits (V1: Pending, V2: Pending, Processing)
      const pendingDepositsQuery = {
        where: {
          type: "Deposit",
          status: { $in: ["Pending", "Processing"] }, // ✅ V2: รองรับทั้ง V1 และ V2
          deletedAt: { $exists: false },
        },
        order: "-createdAt",
        limit: limit,
      };

      const depositsResult = await queryClass(
        "DepositWithdraw",
        pendingDepositsQuery,
        sessionToken
      );
      let pendingDeposits = depositsResult.results || [];
      const viewedDepositIds = viewedAlerts.deposit || [];

      // ✅ Filter out viewed deposits
      pendingDeposits = pendingDeposits.filter(
        (tx: any) => !viewedDepositIds.includes(tx.objectId)
      );
      const pendingDepositsCount = pendingDeposits.length;

      // 2. ดึง Pending Withdraws (V1: Pending, V2: Pending, Processing)
      const pendingWithdrawsQuery = {
        where: {
          type: "Withdraw",
          status: { $in: ["Pending", "Processing"] }, // ✅ V2: รองรับทั้ง V1 และ V2
          deletedAt: { $exists: false },
        },
        order: "-createdAt",
        limit: limit,
      };

      const withdrawsResult = await queryClass(
        "DepositWithdraw",
        pendingWithdrawsQuery,
        sessionToken
      );
      let pendingWithdraws = withdrawsResult.results || [];
      const viewedWithdrawIds = viewedAlerts.withdraw || [];

      // ✅ Filter out viewed withdraws
      pendingWithdraws = pendingWithdraws.filter(
        (tx: any) => !viewedWithdrawIds.includes(tx.objectId)
      );
      const pendingWithdrawsCount = pendingWithdraws.length;

      // 3. ดึงสมาชิกใหม่ (ภายใน 24 ชั่วโมง หรือตามที่กำหนด)
      let newMembers: any[] = [];
      let newMembersCount = 0;

      if (includeNewMembers) {
        const newMembersCutoff = moment()
          .subtract(newMembersHours, "hours")
          .toDate();
        const newMembersQuery = {
          where: {
            deletedAt: { $exists: false },
            type: "customer", // ✅ กรองเฉพาะ customer (ไม่รวม super_admin และ admin)
            createdAt: {
              $gte: {
                __type: "Date",
                iso: newMembersCutoff.toISOString(),
              },
            },
          },
          order: "-createdAt",
          limit: limit,
        };

        const membersResult = await queryClass(
          "_User",
          newMembersQuery,
          sessionToken
        );
        // ✅ ใช้ตัวแปรเดียวกัน (ไม่ shadow)
        newMembers = membersResult.results || [];
        const viewedMemberIds = viewedAlerts.member || [];

        console.log("👥 [AdminAlertService] New members before filter:", {
          total: newMembers.length,
          viewedIds: viewedMemberIds,
          memberIds: newMembers.map((m: any) => m.objectId),
        });

        // ✅ Filter out viewed members
        newMembers = newMembers.filter(
          (member: any) => !viewedMemberIds.includes(member.objectId)
        );
        newMembersCount = newMembers.length;

        console.log("👥 [AdminAlertService] New members after filter:", {
          total: newMembersCount,
          memberIds: newMembers.map((m: any) => m.objectId),
        });
      }

      // 4. แปลงเป็น AdminAlert format
      const alerts: AdminAlert[] = [];

      // Pending Deposits
      pendingDeposits.forEach((tx: any) => {
        const createdAt = tx.createdAt ? new Date(tx.createdAt) : new Date();
        alerts.push({
          id: tx.objectId || `deposit-${Date.now()}`,
          type: "deposit",
          title: "รายการฝากรออนุมัติ",
          description: `${tx.member_str?.username || "N/A"} ฿${(
            tx.amount || 0
          ).toLocaleString()}`,
          amount: tx.amount || 0,
          memberId: tx.member_id?.objectId || tx.member_id,
          memberUsername: tx.member_str?.username,
          transactionId: tx.objectId,
          createdAt: createdAt.toISOString(),
          timeAgo: moment(createdAt).fromNow(),
        });
      });

      // Pending Withdraws
      pendingWithdraws.forEach((tx: any) => {
        const createdAt = tx.createdAt ? new Date(tx.createdAt) : new Date();
        alerts.push({
          id: tx.objectId || `withdraw-${Date.now()}`,
          type: "withdraw",
          title: "รายการถอนรออนุมัติ",
          description: `${tx.member_str?.username || "N/A"} ฿${(
            tx.amount || 0
          ).toLocaleString()}`,
          amount: tx.amount || 0,
          memberId: tx.member_id?.objectId || tx.member_id,
          memberUsername: tx.member_str?.username,
          transactionId: tx.objectId,
          createdAt: createdAt.toISOString(),
          timeAgo: moment(createdAt).fromNow(),
        });
      });

      // New Members
      newMembers.forEach((member: any) => {
        const createdAt = member.createdAt
          ? new Date(member.createdAt)
          : new Date();
        alerts.push({
          id: member.objectId || `member-${Date.now()}`,
          type: "member",
          title: "สมาชิกใหม่",
          description: member.username || member.mobile_phone || "N/A",
          memberId: member.objectId,
          memberUsername: member.username || member.mobile_phone,
          createdAt: createdAt.toISOString(),
          timeAgo: moment(createdAt).fromNow(),
        });
      });

      // เรียงตามเวลา (ใหม่สุดก่อน)
      alerts.sort((a, b) => {
        const timeA = new Date(a.createdAt).getTime();
        const timeB = new Date(b.createdAt).getTime();
        return timeB - timeA;
      });

      // จำกัดจำนวน
      const limitedAlerts = alerts.slice(0, limit);

      const counts: AdminAlertCounts = {
        pendingDeposits: pendingDepositsCount,
        pendingWithdraws: pendingWithdrawsCount,
        newMembers: newMembersCount,
        total: pendingDepositsCount + pendingWithdrawsCount + newMembersCount,
      };

      console.log("✅ [AdminAlertService] Fetched:", {
        deposits: pendingDepositsCount,
        withdraws: pendingWithdrawsCount,
        members: newMembersCount,
        total: counts.total,
        alertsCount: limitedAlerts.length,
        viewedAlerts: viewedAlerts,
        rawDeposits: depositsResult.results?.length || 0,
        rawWithdraws: withdrawsResult.results?.length || 0,
        rawMembers: newMembers?.length || 0,
      });

      // Debug: แสดงรายละเอียด alerts
      if (limitedAlerts.length > 0) {
        console.log(
          "📋 [AdminAlertService] Alerts:",
          limitedAlerts.map((n) => ({
            type: n.type,
            title: n.title,
            description: n.description,
            timeAgo: n.timeAgo,
          }))
        );
      } else {
        console.warn("⚠️ [AdminAlertService] No alerts found after filtering!", {
          totalAlertsBeforeFilter: alerts.length,
          limitedAlerts: limitedAlerts.length,
          viewedAlerts,
          pendingDepositsCount,
          pendingWithdrawsCount,
          newMembersCount,
        });
      }

      return {
        alerts: limitedAlerts,
        counts,
      };
    } catch (error) {
      console.error("❌ [AdminAlertService] Error:", error);
      return {
        alerts: [],
        counts: {
          pendingDeposits: 0,
          pendingWithdraws: 0,
          newMembers: 0,
          total: 0,
        },
      };
    }
  },

  /**
   * ดึงเฉพาะจำนวน (เร็วกว่า)
   */
  async getCounts(sessionToken: string): Promise<AdminAlertCounts> {
    try {
      // ✅ ดึง viewed alerts จาก backend
      let viewedAlerts: Record<string, string[]> = {};
      try {
        viewedAlerts =
          (await callCloudFunction(
            "getViewedNotifications",
            {},
            sessionToken
          )) || {};
      } catch (error) {
        console.warn(
          "⚠️ [AdminAlertService] Failed to fetch viewed alerts in getCounts:",
          error
        );
        // Continue without filtering if error
      }

      // ✅ Filter counts by viewed alerts
      const viewedDepositIds = viewedAlerts.deposit || [];
      const viewedWithdrawIds = viewedAlerts.withdraw || [];
      const viewedMemberIds = viewedAlerts.member || [];

      // ✅ Get actual counts (need to fetch and filter)
      // Note: This is less efficient but necessary for accurate counts
      // Future optimization: Store counts in cache or use aggregation
      const [depositsList, withdrawsList, membersList] = await Promise.all([
        queryClass(
          "DepositWithdraw",
          {
            where: {
              type: "Deposit",
              status: { $in: ["Pending", "Processing"] },
              deletedAt: { $exists: false },
            },
            limit: 1000, // Get all to filter
          },
          sessionToken
        ),
        queryClass(
          "DepositWithdraw",
          {
            where: {
              type: "Withdraw",
              status: { $in: ["Pending", "Processing"] },
              deletedAt: { $exists: false },
            },
            limit: 1000, // Get all to filter
          },
          sessionToken
        ),
        queryClass(
          "_User",
          {
            where: {
              deletedAt: { $exists: false },
              type: "customer",
              createdAt: {
                $gte: {
                  __type: "Date",
                  iso: moment().subtract(24, "hours").toDate().toISOString(),
                },
              },
            },
            limit: 1000, // Get all to filter
          },
          sessionToken
        ),
      ]);

      const pendingDeposits = (depositsList.results || []).filter(
        (tx: any) => !viewedDepositIds.includes(tx.objectId)
      );
      const pendingWithdraws = (withdrawsList.results || []).filter(
        (tx: any) => !viewedWithdrawIds.includes(tx.objectId)
      );
      const newMembers = (membersList.results || []).filter(
        (member: any) => !viewedMemberIds.includes(member.objectId)
      );

      const counts: AdminAlertCounts = {
        pendingDeposits: pendingDeposits.length,
        pendingWithdraws: pendingWithdraws.length,
        newMembers: newMembers.length,
        total:
          pendingDeposits.length + pendingWithdraws.length + newMembers.length,
      };

      return counts;
    } catch (error) {
      console.error("❌ [AdminAlertService] Error getting counts:", error);
      return {
        pendingDeposits: 0,
        pendingWithdraws: 0,
        newMembers: 0,
        total: 0,
      };
    }
  },

  /**
   * Mark alert as viewed
   */
  async markAlertAsViewed(
    sessionToken: string,
    alertType: "member" | "deposit" | "withdraw",
    entityId: string,
    entityType?: string
  ): Promise<void> {
    try {
      await callCloudFunction(
        "markNotificationAsViewed",
        { notificationType: alertType, entityId, entityType },
        sessionToken
      );
      console.log(
        `✅ [AdminAlertService] Marked as viewed: ${alertType} - ${entityId}`
      );
    } catch (error) {
      console.error("❌ [AdminAlertService] Error marking as viewed:", error);
      throw error;
    }
  },

  /**
   * Get viewed alerts (for debugging)
   */
  async getViewedAlerts(
    sessionToken: string
  ): Promise<Record<string, string[]>> {
    try {
      const viewed = await callCloudFunction(
        "getViewedNotifications",
        {},
        sessionToken
      );
      return viewed || {};
    } catch (error) {
      console.error(
        "❌ [AdminAlertService] Error getting viewed alerts:",
        error
      );
      return {};
    }
  },
};

